import React from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import KeyIcon from '@mui/icons-material/Key';
import LinkIcon from '@mui/icons-material/Link';
import PersonIcon from '@mui/icons-material/Person';
import SettingsSuggestIcon from '@mui/icons-material/SettingsSuggest';
import { useTokens } from 'theme';
import Panel from './Panel';
import { fmtRelative, fmtDate } from './format';
import { ChannelBadge } from './channels';

import { useTranslation } from 'react-i18next';

function SourceCell({ log }) {
  const { t } = useTranslation();
  const themeTokens = useTokens();
  if (log.source === 'api_key') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <KeyIcon sx={{ fontSize: 14, color: themeTokens.link }} />
        <Typography sx={{ fontSize: '0.8rem', fontWeight: 600 }}>{log.api_key_name || t('data.apiKey')}</Typography>
        {log.api_key_prefix && (
          <Typography className="mono" sx={{ fontSize: '0.7rem', color: themeTokens.inkMuted }}>{log.api_key_prefix}…</Typography>
        )}
      </Box>
    );
  }
  if (log.source === 'console') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <PersonIcon sx={{ fontSize: 14, color: themeTokens.info }} />
        <Typography sx={{ fontSize: '0.8rem', fontWeight: 600 }}>{log.created_by_email || t('data.console')}</Typography>
      </Box>
    );
  }
  if (log.source === 'connection') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <LinkIcon sx={{ fontSize: 14, color: themeTokens.warning }} />
        <Typography sx={{ fontSize: '0.8rem', fontWeight: 600 }}>{log.connection_name || t('data.connection')}</Typography>
        {log.client && (
          <Typography sx={{ fontSize: '0.7rem', color: themeTokens.inkMuted }}>{log.client}</Typography>
        )}
      </Box>
    );
  }
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
      <SettingsSuggestIcon sx={{ fontSize: 14, color: themeTokens.inkMuted }} />
      <Typography sx={{ fontSize: '0.8rem', color: themeTokens.inkMuted }}>{t('data.system')}</Typography>
    </Box>
  );
}

export default function RecentQueries({ logs, isLoading }) {
  const { t } = useTranslation();
  const themeTokens = useTokens();
  return (
    <Panel icon={<HistoryIcon />} title={t('data.recentQueries')} bodySx={{ p: 0 }}>
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={24} />
        </Box>
      ) : !logs?.length ? (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: themeTokens.inkMuted }}>{t('data.noQueriesYetRunAQueryViaTheApiOrA')}</Typography>
        </Box>
      ) : (
        <Box sx={{ p: 1 }}>
          <TableContainer role="region" aria-label={t('data.recentQueries2')} tabIndex={0}
            sx={{ maxHeight: 360, border: 'none', boxShadow: 'none' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>{t('data.when')}</TableCell>
                  <TableCell>{t('data.from')}</TableCell>
                  <TableCell>{t('data.via')}</TableCell>
                  <TableCell>{t('data.cubeTable')}</TableCell>
                  <TableCell>{t('data.status')}</TableCell>
                  <TableCell align="right">{t('data.rows')}</TableCell>
                  <TableCell align="right">{t('data.duration')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {logs.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell>
                      <Tooltip title={fmtDate(log.created_at)}>
                        <Typography sx={{ fontSize: '0.78rem', color: themeTokens.inkMuted }}>{fmtRelative(log.created_at)}</Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell><SourceCell log={log} /></TableCell>
                    <TableCell><ChannelBadge channel={log.channel} /></TableCell>
                    <TableCell><Typography className="mono" sx={{ fontSize: '0.78rem' }}>{log.cube_name}</Typography></TableCell>
                    <TableCell>
                      {log.status === 'error' ? (
                        <Tooltip title={log.error_message || log.error_code || t('data.error')}>
                          <Chip label={t('data.error2')} size="small" color="error" />
                        </Tooltip>
                      ) : log.status === 'running' ? (
                        <Chip label={t('data.running2')} size="small" />
                      ) : (
                        <Chip label={t('data.success')} size="small" color="success" />
                      )}
                    </TableCell>
                    <TableCell align="right"><Typography sx={{ fontSize: '0.78rem' }}>{log.row_count}</Typography></TableCell>
                    <TableCell align="right"><Typography sx={{ fontSize: '0.78rem', color: themeTokens.inkMuted }}>{log.duration_ms}{t('data.ms')}</Typography></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Panel>
  );
}
